import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import type { SimulationNode } from '../../../models/simulation';
import type { SimulationResultPayload } from '../../../models/simulation';
import { deriveFormulaUnit } from '../../../utils/formulaUnits';
import { useSimulationStore } from '../../../store/simulationStore';
import { HierarchicalTableRow } from './HierarchicalTableRow';

interface ScenariosTableProps {
  nodes: SimulationNode[];
  allNodes: SimulationNode[]; // Full graph - needed for formula unit derivation
  result?: SimulationResultPayload;
  baselineResult?: SimulationResultPayload;
  showComparison?: boolean;
  onNodeClick?: (nodeId: string) => void;
  onNodeDeselect?: (nodeId: string) => void;
  unit?: string; // Optional unit filter - if provided, only show nodes with this unit
  title?: string; // Optional title for the table
}

// Derive unit for nodes
const getNodeUnit = (node: SimulationNode, allNodes: SimulationNode[]): string | undefined => {
  if (node.data.type === 'distribution') {
    return node.data.unit;
  }
  if (node.data.type === 'formula') {
    return deriveFormulaUnit(node, allNodes);
  }
  return undefined;
};

// Get mean value for a node from results
const getNodeMean = (nodeId: string, result?: SimulationResultPayload): number => {
  if (!result || !result.results[nodeId]) {
    return 0;
  }
  const mean = result.results[nodeId].summary?.mean;
  return typeof mean === 'number' && isFinite(mean) ? mean : 0;
};

type SortField = 'name' | 'value' | 'tags' | 'unit';
type SortDirection = 'asc' | 'desc';

interface TableRow {
  nodeId: string;
  name: string;
  tags: string[];
  value: number;
  baselineValue: number;
  unit: string | undefined;
}

interface HierarchicalRow {
  id: string;
  type: 'tag' | 'node';
  name: string;
  tags?: string[];
  value: number;
  baselineValue: number;
  unit?: string;
  nodeId?: string;
  level: number;
  children?: HierarchicalRow[];
  isExpanded?: boolean;
}

const SortArrow = ({ field, sortField, sortDirection }: { field: SortField; sortField: SortField; sortDirection: SortDirection }) => {
  if (sortField !== field) return <span className="sort-arrow">▴</span>;
  return <span className="sort-arrow">{sortDirection === 'asc' ? '▲' : '▼'}</span>;
};

const getTagCounts = (nodes: SimulationNode[]) => {
  const counts = new Map<string, number>();
  nodes.forEach((node) => {
    node.data.tags?.forEach((tag) => {
      counts.set(tag, (counts.get(tag) || 0) + 1);
    });
  });
  return counts;
};

export const ScenariosTable = ({ nodes, allNodes, result, baselineResult, showComparison = true, onNodeClick, onNodeDeselect, unit, title }: ScenariosTableProps) => {
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [editingNodeId, setEditingNodeId] = useState<string | null>(null);
  const isEditingRef = useRef(false);
  
  const [expandedTags, setExpandedTags] = useState<Set<string>>(() => {
    // Initialize with all tags expanded by default
    // We'll generate all possible tag paths by considering all tag combinations
    const allTagPaths = new Set<string>();
    
    nodes.forEach((node) => {
      const tags = node.data.tags && node.data.tags.length > 0 
        ? [...node.data.tags].sort() // Simple alphabetical sort for initialization
        : ['untagged'];
      let currentPath = '';
      tags.forEach((tag: string) => {
        currentPath = currentPath ? `${currentPath}/${tag}` : tag;
        allTagPaths.add(currentPath);
      });
    });
    return allTagPaths;
  });
  const updateNode = useSimulationStore((state) => state.updateNode);

  // Track editing state in ref to prevent unnecessary recalculations
  useEffect(() => {
    isEditingRef.current = editingNodeId !== null;
  }, [editingNodeId]);

  // Filter nodes by unit if specified
  const filteredNodes = useMemo(() => {
    let filtered = nodes;
    if (unit) {
      filtered = filtered.filter((node) => {
        const nodeUnit = getNodeUnit(node, allNodes);
        return nodeUnit === unit;
      });
    }
    return filtered;
  }, [nodes, allNodes, unit]);

  // Transform nodes into table rows
  const tableRows = useMemo(() => {
    return filteredNodes.map((node): TableRow => {
      const meanValue = getNodeMean(node.id, result);
      const baselineMeanValue = getNodeMean(node.id, baselineResult);
      return {
        nodeId: node.id,
        name: node.data.name,
        tags: node.data.tags || [],
        value: meanValue,
        baselineValue: baselineMeanValue,
        unit: getNodeUnit(node, allNodes)
      };
    });
  }, [filteredNodes, result, baselineResult, allNodes]);

  // Build hierarchical structure
  // Use a ref to store the last computed value to prevent unnecessary recalculations during editing
  const lastHierarchicalRows = useRef<HierarchicalRow[]>([]);
  const lastFilteredNodesSnapshot = useRef<string>('');
  
  /**
   * Builds a hierarchical structure that maximizes group sizes.
   * At each level, chooses the tag that groups the most nodes together.
   */
  const buildOptimalHierarchy = useCallback((
    nodes: Array<{ node: SimulationNode; value: number; baselineValue: number; unit?: string }>,
    level: number = 0,
    usedTags: Set<string> = new Set(),
    pathPrefix: string = ''
  ): HierarchicalRow[] => {
    if (nodes.length === 0) return [];

    // If all nodes have no more tags, create leaf nodes
    const nodesWithTags = nodes.filter(({ node }) => {
      const tags = node.data.tags || [];
      return tags.some(tag => !usedTags.has(tag));
    });

    if (nodesWithTags.length === 0) {
      // All nodes are fully categorized, create leaf nodes
      return nodes.map(({ node, value, baselineValue, unit }) => ({
        id: node.id,
        type: 'node' as const,
        name: node.data.name,
        tags: node.data.tags,
        value,
        baselineValue,
        unit,
        nodeId: node.id,
        level,
        isExpanded: expandedTags.has(node.id)
      }));
    }

    // Calculate tag frequencies for remaining tags
    const tagCounts = new Map<string, number>();
    nodesWithTags.forEach(({ node }) => {
      const tags = node.data.tags || [];
      tags.forEach(tag => {
        if (!usedTags.has(tag)) {
          tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
        }
      });
    });

    // Sort tags by frequency (descending), then alphabetically
    const sortedTags = Array.from(tagCounts.entries())
      .sort((a, b) => {
        if (b[1] !== a[1]) return b[1] - a[1]; // Higher count first
        return a[0].localeCompare(b[0]); // Alphabetical tiebreaker
      })
      .map(([tag]) => tag);

    const result: HierarchicalRow[] = [];
    let remainingNodes = nodesWithTags;

    // Group nodes by the most common tag
    for (const tag of sortedTags) {
      const nodesWithTag = remainingNodes.filter(({ node }) => {
        const tags = node.data.tags || [];
        return tags.includes(tag);
      });

      if (nodesWithTag.length === 0) continue;

      // Create path for this tag
      const currentPath = pathPrefix ? `${pathPrefix}/${tag}` : tag;

      // Create tag row
      const tagRow: HierarchicalRow = {
        id: currentPath,
        type: 'tag',
        name: tag,
        value: 0,
        baselineValue: 0,
        level,
        children: [],
        isExpanded: expandedTags.has(currentPath)
      };

      // Recursively build children with this tag marked as used
      const nextUsedTags = new Set(usedTags);
      nextUsedTags.add(tag);
      
      const children = buildOptimalHierarchy(nodesWithTag, level + 1, nextUsedTags, currentPath);
      tagRow.children = children;

      // Calculate sum for this tag group
      tagRow.value = children.reduce((sum, child) => sum + child.value, 0);
      tagRow.baselineValue = children.reduce((sum, child) => sum + child.baselineValue, 0);
      
      // Infer unit from children
      const childUnits = children
        .map((child) => child.unit)
        .filter((u): u is string => u !== undefined);
      if (childUnits.length > 0) {
        tagRow.unit = childUnits[0];
      }

      result.push(tagRow);

      // Remove nodes that were grouped under this tag
      const nodeIdsWithTag = new Set(nodesWithTag.map(({ node }) => node.id));
      remainingNodes = remainingNodes.filter(({ node }) => !nodeIdsWithTag.has(node.id));
    }

    // Handle nodes without any remaining tags (shouldn't happen, but safety check)
    const finalRemainingNodes = nodes.filter(({ node }) => {
      const tags = node.data.tags || [];
      return !tags.some(tag => !usedTags.has(tag));
    });

    if (finalRemainingNodes.length > 0) {
      const leafNodes = finalRemainingNodes.map(({ node, value, baselineValue, unit }) => ({
        id: node.id,
        type: 'node' as const,
        name: node.data.name,
        tags: node.data.tags,
        value,
        baselineValue,
        unit,
        nodeId: node.id,
        level,
        isExpanded: expandedTags.has(node.id)
      }));
      result.push(...leafNodes);
    }

    return result;
  }, [expandedTags]);

  const hierarchicalRows = useMemo(() => {
    // Create a stable snapshot of filtered nodes (excluding the node being edited)
    const nodesSnapshot = filteredNodes
      .map(n => `${n.id}:${n.data.name}:${JSON.stringify(n.data.tags || [])}`)
      .join('|');
    
    // If we're editing and the snapshot hasn't changed (except for the editing node), 
    // return the last computed value to prevent re-renders
    if (isEditingRef.current && nodesSnapshot === lastFilteredNodesSnapshot.current) {
      return lastHierarchicalRows.current;
    }
    
    lastFilteredNodesSnapshot.current = nodesSnapshot;
    
    // Prepare nodes with their values and units
    const nodesWithData = filteredNodes.map((node) => ({
      node,
      value: getNodeMean(node.id, result),
      baselineValue: getNodeMean(node.id, baselineResult),
      unit: getNodeUnit(node, allNodes)
    }));

    // Handle untagged nodes separately
    const taggedNodes = nodesWithData.filter(({ node }) => 
      node.data.tags && node.data.tags.length > 0
    );
    const untaggedNodes = nodesWithData.filter(({ node }) => 
      !node.data.tags || node.data.tags.length === 0
    );

    const rootRows: HierarchicalRow[] = [];

    // Build hierarchy for tagged nodes
    if (taggedNodes.length > 0) {
      const taggedHierarchy = buildOptimalHierarchy(taggedNodes);
      rootRows.push(...taggedHierarchy);
    }

    // Handle untagged nodes
    if (untaggedNodes.length > 0) {
      const untaggedPath = 'untagged';
      let untaggedRow = rootRows.find(row => row.id === untaggedPath && row.type === 'tag');
      
      if (!untaggedRow) {
        untaggedRow = {
          id: untaggedPath,
          type: 'tag',
          name: 'untagged',
          value: 0,
          baselineValue: 0,
          level: 0,
          children: [],
          isExpanded: expandedTags.has(untaggedPath)
        };
        rootRows.push(untaggedRow);
      }

      const untaggedChildren = untaggedNodes.map(({ node, value, baselineValue, unit }) => ({
        id: node.id,
        type: 'node' as const,
        name: node.data.name,
        tags: node.data.tags,
        value,
        baselineValue,
        unit,
        nodeId: node.id,
        level: 1,
        isExpanded: expandedTags.has(node.id)
      }));

      if (untaggedRow.children) {
        untaggedRow.children.push(...untaggedChildren);
      } else {
        untaggedRow.children = untaggedChildren;
      }

      untaggedRow.value = untaggedChildren.reduce((sum, child) => sum + child.value, 0);
      untaggedRow.baselineValue = untaggedChildren.reduce((sum, child) => sum + child.baselineValue, 0);
      const untaggedUnits = untaggedChildren
        .map((child) => child.unit)
        .filter((u): u is string => u !== undefined);
      if (untaggedUnits.length > 0) {
        untaggedRow.unit = untaggedUnits[0];
      }
    }
    
    lastHierarchicalRows.current = rootRows;
    return rootRows;
  }, [filteredNodes, result, baselineResult, allNodes, expandedTags, buildOptimalHierarchy]);

  // Flatten hierarchical rows for display (respecting expand/collapse state)
  const flattenedRows = useMemo(() => {
    const flattened: HierarchicalRow[] = [];
    
    const flatten = (rows: HierarchicalRow[]) => {
      rows.forEach((row) => {
        flattened.push(row);
        if (row.isExpanded && row.children && row.children.length > 0) {
          flatten(row.children);
        }
      });
    };
    
    flatten(hierarchicalRows);
    return flattened;
  }, [hierarchicalRows]);

  // Toggle tag expansion
  const toggleTag = useCallback((tagPath: string) => {
    setExpandedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tagPath)) {
        next.delete(tagPath);
      } else {
        next.add(tagPath);
      }
      return next;
    });
  }, []);

  // Sort rows
  const sortedRows = useMemo(() => {
    // For hierarchical display, we don't sort - we keep the tree structure
    // Sorting would break the hierarchy
    return flattenedRows;
  }, [flattenedRows]);

  // Keep old sorted flat rows for comparison (not used in hierarchical view)
  const sortedFlatRows = useMemo(() => {
    const sorted = [...tableRows];
    sorted.sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'value':
          comparison = a.value - b.value;
          break;
        case 'tags':
          const aTags = a.tags.join(', ');
          const bTags = b.tags.join(', ');
          comparison = aTags.localeCompare(bTags);
          break;
        case 'unit':
          const aUnit = a.unit || '';
          const bUnit = b.unit || '';
          comparison = aUnit.localeCompare(bUnit);
          break;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [tableRows, sortField, sortDirection]);

  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField]);

  const handleRowClick = useCallback((nodeId: string) => {
    if (onNodeClick) {
      onNodeClick(nodeId);
    }
  }, [onNodeClick]);

  const handleTagSave = useCallback((nodeId: string, tags: string[]) => {
    // Ensure tags is always an array
    const normalizedTags = Array.isArray(tags) ? tags : [];
    updateNode(nodeId, (node) => {
      node.data.tags = normalizedTags.length > 0 ? normalizedTags : undefined;
    });
    setEditingNodeId(null);
  }, [updateNode]);



  if (filteredNodes.length === 0) {
    return (
      <div className="scenarios-table-empty">
        <p>No data available for table view.</p>
        <p className="scenarios-table-hint">
          {filteredNodes.length === 0
            ? unit
              ? `No nodes found with unit "${unit}".`
              : 'Add nodes with tags to see them in the table.'
            : !result
            ? 'Run a simulation to see table view with mean values.'
            : 'No nodes to display.'}
        </p>
      </div>
    );
  }

  return (
    <div className="scenarios-table-container">
      {title && (
        <div className="scenarios-table-title">
          <h3>{title}</h3>
        </div>
      )}
      <div className="scenarios-table-wrapper">
        <table className="scenarios-table">
          <colgroup>
            <col className="scenarios-col-deselect" />
            <col className="scenarios-col-name" />
            <col className="scenarios-col-tags" />
            <col className="scenarios-col-value" />
            <col className="scenarios-col-unit" />
          </colgroup>
          <thead>
            <tr>
              <th className="scenarios-table-header-deselect"></th>
              <th
                className={`sortable ${sortField === 'name' ? 'sort-active' : ''}`}
                onClick={() => handleSort('name')}
              >
                <span>Name</span>
                <SortArrow field="name" sortField={sortField} sortDirection={sortDirection} />
              </th>
              <th
                className={`sortable ${sortField === 'tags' ? 'sort-active' : ''}`}
                onClick={() => handleSort('tags')}
              >
                <span>Tags</span>
                <SortArrow field="tags" sortField={sortField} sortDirection={sortDirection} />
              </th>
              <th
                className={`sortable ${sortField === 'value' ? 'sort-active' : ''}`}
                onClick={() => handleSort('value')}
              >
                <span>Value</span>
                <SortArrow field="value" sortField={sortField} sortDirection={sortDirection} />
              </th>
              <th
                className={`sortable ${sortField === 'unit' ? 'sort-active' : ''}`}
                onClick={() => handleSort('unit')}
              >
                <span>Unit</span>
                <SortArrow field="unit" sortField={sortField} sortDirection={sortDirection} />
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => {
              const isEditing = row.nodeId ? editingNodeId === row.nodeId : false;
              return (
                <HierarchicalTableRow
                  key={row.id}
                  row={row}
                  showComparison={showComparison}
                  onToggle={toggleTag}
                  onNodeClick={handleRowClick}
                  onNodeDeselect={onNodeDeselect}
                  onEditStart={(nodeId) => setEditingNodeId(nodeId)}
                  onEditCancel={() => setEditingNodeId(null)}
                  onEditSave={handleTagSave}
                  isEditing={isEditing}
                  allNodes={allNodes}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

